import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search, Target, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtMoney, STAGES, STAGE_LABEL } from "@/lib/finance";

export default function Deals() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [stage, setStage] = useState("all");
  const [closed, setClosed] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (stage !== "all") params.stage = stage;
      if (closed !== "all") params.closed = closed === "closed";
      if (q) params.q = q;
      const r = await api.get("/deals", { params });
      setItems(r.data);
    } finally {
      setLoading(false);
    }
  }, [stage, closed, q]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!q) return items;
    const s = q.toLowerCase();
    return items.filter((d) =>
      d.name?.toLowerCase().includes(s) ||
      d.reference?.toLowerCase().includes(s) ||
      d.client_name?.toLowerCase().includes(s)
    );
  }, [items, q]);

  const remove = async (d) => {
    if (!window.confirm(`Supprimer la transaction ${d.reference} ?`)) return;
    try {
      await api.delete(`/deals/${d.id}`);
      toast.success("Transaction supprimée");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="deals-list">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-slate-500 font-semibold">Pipeline commercial</div>
          <h1 className="mt-2 font-display text-4xl text-brand-navy tracking-tight">Transactions</h1>
          <p className="mt-1 text-sm text-slate-500">
            {items.length} transaction(s) — suivez les opportunités, paiements, marges et commissions.
          </p>
        </div>
        <Button asChild className="bg-brand-orange hover:bg-brand-orangeDark text-white" data-testid="new-deal-btn">
          <Link to="/admin/deals/new"><Plus className="h-4 w-4 mr-1.5" /> Nouvelle transaction</Link>
        </Button>
      </div>

      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              className="pl-9"
              placeholder="Rechercher par nom, référence, client…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="deals-search"
            />
          </div>
          <Select value={stage} onValueChange={setStage}>
            <SelectTrigger data-testid="filter-stage"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les stades</SelectItem>
              {STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={closed} onValueChange={setClosed}>
            <SelectTrigger data-testid="filter-closed"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Ouverts + clôturés</SelectItem>
              <SelectItem value="open">Ouverts uniquement</SelectItem>
              <SelectItem value="closed">Clôturés uniquement</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card>
        <Table data-testid="deals-table">
          <TableHeader>
            <TableRow>
              <TableHead>Référence</TableHead>
              <TableHead>Transaction</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Offre</TableHead>
              <TableHead className="text-right">Prix</TableHead>
              <TableHead className="text-right">Commission brute</TableHead>
              <TableHead className="text-right">Dépenses</TableHead>
              <TableHead className="text-right">Marge nette</TableHead>
              <TableHead>Stade</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={10} className="text-center text-slate-500 py-10">Chargement…</TableCell></TableRow>
            )}
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} className="text-center py-14">
                  <Target className="h-8 w-8 text-slate-300 mx-auto" />
                  <div className="mt-3 text-sm text-slate-500">Aucun deal pour ce filtre.</div>
                  <Link to="/admin/deals/new" className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-brand-orange hover:underline">
                    <Plus className="h-4 w-4" /> Créer un Deal
                  </Link>
                </TableCell>
              </TableRow>
            )}
            {filtered.map((d) => (
              <TableRow key={d.id} data-testid={`deal-row-${d.id}`} className="cursor-pointer hover:bg-slate-50" onClick={() => nav(`/admin/deals/${d.id}`)}>
                <TableCell className="font-mono text-xs">{d.reference}</TableCell>
                <TableCell className="font-semibold text-brand-navy">{d.name}</TableCell>
                <TableCell>{d.client_name || "—"}</TableCell>
                <TableCell className="text-xs text-slate-500 truncate max-w-xs">{d.listing_snapshot?.title || "—"}</TableCell>
                <TableCell className="text-right font-medium">{fmtMoney(d.finance?.price, d.currency)}</TableCell>
                <TableCell className="text-right text-brand-orange font-semibold">{fmtMoney(d.finance?.commission_brute, d.currency)}</TableCell>
                <TableCell className="text-right text-slate-500">{fmtMoney(d.finance?.expenses_total, d.currency)}</TableCell>
                <TableCell className={`text-right font-bold ${d.finance?.margin_net >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                  {fmtMoney(d.finance?.margin_net, d.currency)}
                </TableCell>
                <TableCell>
                  <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-700">{STAGE_LABEL[d.stage] || d.stage}</span>
                  {d.closed && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-bold uppercase tracking-widest">clôturé</span>}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => { e.stopPropagation(); remove(d); }}
                    data-testid={`delete-deal-${d.id}`}
                    disabled={d.closed}
                  >
                    <Trash2 className={`h-4 w-4 ${d.closed ? "text-slate-300" : "text-red-500"}`} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
